import React, { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import AuthHeader from '../../components/AuthHeader'
import api from '../../utils/api'
import toast from 'react-hot-toast'

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [passwordReset, setPasswordReset] = useState(false)

  const token = searchParams.get('token')

  // Check if both password fields are filled
  const isFormValid = password.trim() !== '' && confirmPassword.trim() !== ''

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false)
        return
      }

      try {
        const response = await api.get(`/password-reset/verify-reset-token/${token}`)
        setTokenValid(response.data.success)
      } catch (error) {
        console.error('Token verification error:', error)
        setTokenValid(false)
      } finally {
        setVerifying(false)
      }
    }

    verifyToken()
  }, [token])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters long')
      return
    }

    setLoading(true)

    try {
      const response = await api.post('/password-reset/reset-password', {
        token,
        newPassword: password
      })

      if (response.data.success) {
        setPasswordReset(true)
        toast.success('Password reset successfully!')
      } else {
        toast.error(response.data.message || 'Failed to reset password')
      }
    } catch (error) {
      console.error('Reset password error:', error)
      toast.error(error.response?.data?.message || 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  if (verifying) {
    return (
      <div className="min-h-screen bg-black">
        <div className='p-4'>
          <div className="border-2 border-white">
            <AuthHeader />
            <div className='bg-[#141414] p-4 w-full min-h-screen flex items-center justify-center'>
              <div className="w-full max-w-sm relative">
                <div className="bg-card border border-gray-700 p-6 relative">
                  <div className="text-center">
                    <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-white font-avenir">Verifying reset token...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-black">
        <div className='p-4'>
          <div className="border-2 border-white">
            <AuthHeader />
            <div className='bg-[#141414] p-4 w-full min-h-screen flex items-center justify-center'>
              <div className="w-full max-w-sm relative">
                <div className="bg-card border border-gray-700 p-6 relative">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    
                    <h1 className="text-2xl font-bold text-white mb-2 font-avenir">Invalid Token</h1>
                    <p className="text-gray-300 mb-6">
                      This password reset link is invalid or has expired.
                    </p>
                    
                    <div className="space-y-4">
                      <Link
                        to="/forgot-password"
                        className="block w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded transition-colors font-avenir text-center"
                      >
                        Request New Reset Link
                      </Link>
                      
                      <Link
                        to="/login"
                        className="block w-full text-center text-gray-400 hover:text-white transition-colors font-avenir"
                      >
                        Back to Login
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (passwordReset) {
    return (
      <div className="min-h-screen bg-black">
        <div className='p-4'>
          <div className="border-2 border-white">
            <AuthHeader />
            <div className='bg-[#141414] p-4 w-full min-h-screen flex items-center justify-center'>
              <div className="w-full max-w-sm relative">
                <div className="bg-card border border-gray-700 p-6 relative">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    
                    <h1 className="text-2xl font-bold text-white mb-2 font-avenir">Password Reset!</h1>
                    <p className="text-gray-300 mb-6">
                      Your password has been successfully reset. You can now sign in with your new password.
                    </p>
                    
                    <Link
                      to="/login"
                      className="block w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded transition-colors font-avenir text-center"
                    >
                      Sign In
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black">
      <div className='p-4'>
        <div className="border-2 border-white">
          <AuthHeader />
          <div className='bg-[#141414] p-4 w-full min-h-screen flex items-center justify-center'>
            <div className="w-full max-w-sm relative">
              {/* Form Card */}
              <div className="bg-card border border-gray-700 p-6 relative">
                {/* Header */}
                <div className="text-center mb-6">
                  <h1 className="text-2xl font-bold text-white mb-2 font-avenir">Reset Password</h1>
                  <p className="text-gray-400 text-sm">
                    Enter your new password below.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* New Password Field */}
                  <div className="space-y-1">
                    <label htmlFor="password" className="block text-xs font-medium text-gray-300 uppercase tracking-wide font-avenir">
                      New Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full h-10 px-3 py-2 bg-white border-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all font-avenir"
                      placeholder="Enter new password"
                      required
                      disabled={loading}
                      minLength={6}
                    />
                  </div>

                  {/* Confirm Password Field */}
                  <div className="space-y-1">
                    <label htmlFor="confirmPassword" className="block text-xs font-medium text-gray-300 uppercase tracking-wide font-avenir">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full h-10 px-3 py-2 bg-white border-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all font-avenir"
                      placeholder="Confirm new password"
                      required
                      disabled={loading}
                      minLength={6}
                    />
                  </div>

                  {/* Submit Button */}
                  <div className='!mx-6'>
                    <button
                      type="submit"
                      disabled={loading || !isFormValid}
                      className="w-full h-10 bg-red-600 hover:bg-red-700 disabled:bg-red-700 disabled:cursor-not-allowed text-white font-bold text-sm rounded-lg transition-colors flex items-center justify-center uppercase tracking-wide font-avenir"
                    >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Resetting...</span>
                      </div>
                    ) : (
                      'Reset Password'
                    )}
                    </button>
                  </div>
                </form>

                {/* Footer Link */}
                <div className="mt-6 text-center">
                  <Link
                    to="/login"
                    className="text-sm text-gray-400 hover:text-white transition-colors font-avenir"
                  >
                    Back to Login
                  </Link>
                </div>

                {/* Privacy & Terms Links */}
                <div className="mt-6 pt-4 border-t border-gray-700 flex flex-wrap justify-center gap-3 text-xs">
                  <Link
                    to="/privacy-policy"
                    className="text-gray-400 hover:text-red-300 transition-colors font-avenir"
                  >
                    Privacy Policy
                  </Link>
                  <span className="text-gray-600">|</span>
                  <Link
                    to="/terms-of-use"
                    className="text-gray-400 hover:text-red-300 transition-colors font-avenir"
                  >
                    Terms of Use
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage